import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import "./AdministratorSidebar.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { FaSearch } from "react-icons/fa";
import logo from "./assets/logo.jpg";

const SuperAdminSidebar = () => {
  const location = useLocation(); // ← needed to know current page
  const [isSidebarActive, setIsSidebarActive] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [userFullName, setUserFullName] = useState("");
  const [userType, setUserType] = useState("");
  const [userImage, setUserImage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  const getDefaultAvatar = (name) => {
    if (!name) return "https://ui-avatars.com/api/?name=User&background=F6CA13&color=2B363C&size=80";
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=F6CA13&color=2B363C&size=80`;
  };

  const matchesSearch = (text) =>
    text.toLowerCase().includes(searchTerm.toLowerCase());

  const toggleSidebar = () => {
    setIsSidebarActive(!isSidebarActive);
  };

  // Toggle only when user clicks the parent menu
  const toggleSubmenu = (submenu) => {
    if (window.innerWidth <= 768) {
      setIsSidebarActive(true);
    }
    setOpenSubmenu((prev) => (prev === submenu ? null : submenu));
  };

  // ========== AUTO-OPEN SUBMENU BASED ON CURRENT ROUTE ==========
  useEffect(() => {
    const path = location.pathname;

    if (path.startsWith("/vehicles") || path.startsWith("/add-vehicle")) {
      setOpenSubmenu("vehicles");
    } else if (path.startsWith("/view-sales") || path.startsWith("/add-new-sale")) {
      setOpenSubmenu("sales");
    } else if (path.startsWith("/documents-shipping")) {
      setOpenSubmenu("docs");
    } else if (
      path.startsWith("/volet") ||
      path.startsWith("/create-volet") ||
      path.startsWith("/view-payments") ||
      path.startsWith("/create-allocate")
    ) {
      setOpenSubmenu("payments");
    } else if (
      path.startsWith("/view-user") ||
      path.startsWith("/create-account") ||
      path.startsWith("/view-groups") ||
      path.startsWith("/create-group")
    ) {
      setOpenSubmenu("settings");
    }
    // You can add more conditions if needed
  }, [location.pathname]);
  // ==============================================================

  const fetchUserProfile = async () => {
    const userId = localStorage.getItem("userId") || localStorage.getItem("user_id");

    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch("https://skcapi.btcjapan.co/get_user_profile.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: userId }),
      });
      const data = await response.json();

      if (data.Status === "200" && data.User) {
        let fullName = "User";
        if (data.User.fname && data.User.lname) {
          fullName = `${data.User.fname} ${data.User.lname}`;
          localStorage.setItem("fname", data.User.fname);
          localStorage.setItem("lname", data.User.lname);
        } else if (data.User.fname) {
          fullName = data.User.fname;
          localStorage.setItem("fname", data.User.fname);
        }
        setUserFullName(fullName);

        if (data.User.usertype) {
          setUserType(data.User.usertype);
          localStorage.setItem("usertype", data.User.usertype);
        }

        if (data.User.image_url) {
          setUserImage(data.User.image_url);
        } else {
          setUserImage(getDefaultAvatar(fullName));
        }
      } else {
        const storedFName = localStorage.getItem("fname") || "";
        const storedLName = localStorage.getItem("lname") || "";
        const fullName = `${storedFName} ${storedLName}`.trim() || "User";
        setUserFullName(fullName);
        setUserImage(getDefaultAvatar(fullName));
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const storedFName = localStorage.getItem("fname") || "";
    const storedLName = localStorage.getItem("lname") || "";
    const fullName = `${storedFName} ${storedLName}`.trim() || "User";
    setUserFullName(fullName);
    setUserImage(getDefaultAvatar(fullName));
    fetchUserProfile();
  }, []);

  return (
    <div className="wrapper">
      <button
        type="button"
        id="sidebarCollapse"
        className="btn btn-info d-md-none"
        onClick={toggleSidebar}
      >
        <i className="fas fa-bars"></i>
      </button>

      <nav id="sidebar" className={isSidebarActive ? "active" : ""}>
        <div className="sidebar-header">
          <div className="admin-brand">
            <img src={logo} alt="Logo" className="logo" />
          </div>

          <div className="user-brand">
            <img
              src={userImage || getDefaultAvatar(userFullName || "User")}
              alt={userFullName || "User"}
              className="userimg"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = getDefaultAvatar(userFullName || "User");
              }}
            />
          </div>

          <p className="user-name">{userFullName || "User"}</p>

          <div className="searchbar">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search in menu..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <ul className="list-unstyled components">
          {/* Dashboard */}
          {matchesSearch("Dashboard") && (
            <li>
              <NavLink
                to="/admin-panel"
                className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
              >
                <i className="fas fa-tachometer-alt"></i>
                Dashboard
              </NavLink>
            </li>
          )}

          {/* Leads */}
          {matchesSearch("Leads") && (
            <li>
              <NavLink
                to="/view-leads"
                className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
              >
                <i className="fa fa-rocket"></i>
                Leads
              </NavLink>
            </li>
          )}

          {/* Client */}
          {matchesSearch("Client") && (
            <li>
              <NavLink
                to="/view-client"
                className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
              >
                <i className="fa fa-users"></i>
                Client
              </NavLink>
            </li>
          )}

          {/* Bids */}
          {matchesSearch("Bid") && (
            <li>
              <NavLink
                to="/view-bids"
                className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
              >
                <i className="fa fa-bug"></i>
                Bids
              </NavLink>
            </li>
          )}

          {/* Stock */}
          {matchesSearch("Stock") && (
            <li>
              <NavLink
                to="/view-stock"
                className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
              >
                <i className="fa fa-folder-open"></i>
                Stock
              </NavLink>
            </li>
          )}

          {/* Sale Order */}
          {matchesSearch("SaleOrder") && (
            <li>
              <NavLink
                to="/view-sale-order"
                className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
              >
                <i className="fa fa-calendar-times"></i>
                Sale Order
              </NavLink>
            </li>
          )}

          {/* Invoice */}
          {matchesSearch("Invoice") && (
            <li>
              <NavLink
                to="/view-invoice"
                className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
              >
                <i className="fa fa-list"></i>
                Invoice
              </NavLink>
            </li>
          )}

          {matchesSearch("ClientReport") && (
            <li className={openSubmenu === "ClientReport" ? "active" : ""}>
              <a
                href="#!"
                className="nav-link dropdown-toggle"
                onClick={(e) => {
                  e.preventDefault();
                  toggleSubmenu("ClientReport");
                }}
              >
                <i class="fa fa-flag" aria-hidden="true"></i> Client Report
              </a>
              {openSubmenu === "ClientReport" && (
                <ul className="list-unstyled">
                  <li>
                    <NavLink
                      to="/client-report/transactions"
                      className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
                    >
                      Transactions
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/client-report/transactions-report"
                      className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
                    >
                      Transactions Report
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/client-report/transfer-report"
                      className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
                    >
                      Transfer Report
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/client-report/balance-sheet"
                      className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
                    >
                      Balance Sheet
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/client-report/bank-cash"
                      className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
                    >
                      Bank Cash
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/"
                      className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
                    >
                      Deposit
                    </NavLink>
                  </li>
                </ul>
              )}
            </li>
          )}
          

          
          {/* ========== VEHICLES ========== */}
          {/*

          {(matchesSearch("Vehicles") ||
            matchesSearch("View Vehicle List") ||
            matchesSearch("Add New Vehicle")) && (
            <li className={openSubmenu === "vehicles" ? "active" : ""}>
              <a
                href="#!"
                className="nav-link dropdown-toggle"
                onClick={(e) => {
                  e.preventDefault();
                  toggleSubmenu("vehicles");
                }}
              >
                <i className="fas fa-car"></i>
                Vehicles Details
              </a>
              {(openSubmenu === "vehicles" || searchTerm) && (
                <ul className="list-unstyled">
                  {matchesSearch("View Vehicle List") && (
                    <li>
                      <NavLink
                        to="/vehicles"
                        className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
                      >
                        View Vehicle List
                      </NavLink>
                    </li>
                  )}
                  {matchesSearch("Add New Vehicle") && (
                    <li>
                      <NavLink
                        to="/add-vehicle"
                        className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
                      >
                        Add New Vehicle
                      </NavLink>
                    </li>
                  )}
                </ul>
              )}
            </li>
          )}

          */}

          {/* ========== SALES ========== */}
          {/*

          <li className={openSubmenu === "sales" ? "active" : ""}>
            <a
              href="#!"
              className="nav-link dropdown-toggle"
              onClick={(e) => {
                e.preventDefault();
                toggleSubmenu("sales");
              }}
            >
              <i className="fas fa-dollar-sign"></i> Sales Details
            </a>
            {openSubmenu === "sales" && (
              <ul className="list-unstyled">
                <li>
                  <NavLink
                    to="/view-sales"
                    className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
                  >
                    View All Sales
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/add-new-sale"
                    className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
                  >
                    Create New Sale
                  </NavLink>
                </li>
              </ul>
            )}
          </li>

          */}

          {/* ========== DOCUMENTS ========== */}
          {/*

          <li className={openSubmenu === "docs" ? "active" : ""}>
            <a
              href="#!"
              className="nav-link dropdown-toggle"
              onClick={(e) => {
                e.preventDefault();
                toggleSubmenu("docs");
              }}
            >
              <i className="fas fa-file-alt"></i> Documents & Shipping
            </a>
            {openSubmenu === "docs" && (
              <ul className="list-unstyled">
                <li>
                  <NavLink
                    to="/documents-shipping"
                    className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
                  >
                    Documents/Shipping
                  </NavLink>
                </li>
              </ul>
            )}
          </li>
          */}

          {/* ========== PAYMENTS ========== */}
          {/*

          <li className={openSubmenu === "payments" ? "active" : ""}>
            <a
              href="#!"
              className="nav-link dropdown-toggle"
              onClick={(e) => {
                e.preventDefault();
                toggleSubmenu("payments");
              }}
            >
              <i className="fas fa-wallet"></i> Payments
            </a>
            {openSubmenu === "payments" && (
              <ul className="list-unstyled">
                <li>
                  <NavLink
                    to="/volet"
                    className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
                  >
                    View Wallet
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/create-volet"
                    className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
                  >
                    Receive Wallet Amount
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/view-payments"
                    className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
                  >
                    View Allocated Payments
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/create-allocate"
                    className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
                  >
                    Add Allocated Amount
                  </NavLink>
                </li>
              </ul>
            )}
          </li>

          */}

          {/* Accounts */}
          {/*

          {matchesSearch("Accounts") && (
            <li>
              <NavLink
                to="/accounts"
                className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
              >
                <i className="fas fa-users"></i>
                Accounts
              </NavLink>
            </li>
          )}

          */}

          {/* ========== USERS / SETTINGS ========== */}
          <li className={openSubmenu === "settings" ? "active" : ""}>
            <a
              href="#!"
              className="nav-link dropdown-toggle"
              onClick={(e) => {
                e.preventDefault();
                toggleSubmenu("settings");
              }}
            >
              <i className="fa fa-user"></i> Users
            </a>
            {openSubmenu === "settings" && (
              <ul className="list-unstyled">
                <li>
                  <NavLink
                    to="/view-user"
                    className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
                  >
                    All Users
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/create-account"
                    className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
                  >
                    Create User
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/view-groups"
                    className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
                  >
                    View Group
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/create-group"
                    className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
                  >
                    Create Group
                  </NavLink>
                </li>
              </ul>
            )}
          </li>

          {/* Profile */}
          {matchesSearch("Profile") && (
            <li>
              <NavLink
                to="/portfolio"
                className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}
              >
                <i className="fa fa-user-md"></i>
                Profile
              </NavLink>
            </li>
          )}

          <li>
              <NavLink to="/view-chat-admin" className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}>
                  <i className="fas fa-shield-alt" />
                  Chat Audit
              </NavLink>
          </li>

          <li>
              <NavLink to="/announcements" className={({ isActive }) => `nav-link ${isActive ? "active-link" : ""}`}>
                  <i className="fas fa-bullhorn" />
                  Announcements
              </NavLink>
          </li>
        </ul>
      </nav>

      {isSidebarActive && <div className="overlay" onClick={toggleSidebar}></div>}
    </div>
  );
};

export default SuperAdminSidebar;